import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { LockIcon, RefreshCwIcon, SparklesIcon, XIcon } from 'lucide-react';
import type { Aggregates } from '../../types/pulse';
import { buildInterpretation, type Interpretation } from '../../utils/interpretation';
interface InterpretationPanelProps {
  aggregates: Aggregates;
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function InterpretationPanel({
  aggregates,
  onClose
}: InterpretationPanelProps) {
  const [result, setResult] = useState<Interpretation | null>(null);
  const [running, setRunning] = useState(true);
  const run = useCallback(() => {
    setRunning(true);
    const timer = window.setTimeout(() => {
      setResult(buildInterpretation(aggregates));
      setRunning(false);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [aggregates]);
  useEffect(() => run(), [run]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18,
    ease: EASE
  }} className="fixed inset-0 z-[60] flex justify-end bg-navy/50 backdrop-blur-[2px]" role="dialog" aria-modal="true" aria-label="AI interpretation for internal users" data-id="element-95" data-mp="241edxtrqaxp8">
      <motion.aside initial={{
      x: 32,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} exit={{
      x: 24,
      opacity: 0
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="flex h-full w-full max-w-md flex-col overflow-hidden bg-paper shadow-soft" data-id="element-96" data-mp="2ygisxt7eeyqs">
        <header className="shrink-0 bg-navy px-6 py-5" data-id="element-97" data-mp="3cbl52y2u9kc1">
          <div className="flex items-start justify-between gap-4" data-id="element-98" data-mp="3h9wbszxu7eq1">
            <div className="min-w-0" data-id="element-99" data-mp="3m7o8e79ffp0k">
              <p className="flex items-center gap-1.5 text-[9px] uppercase tracking-micro text-mint" data-id="element-100" data-mp="3m6mqbitzu29c">
                <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-101" data-mp="1mwemp8hsa9sw" />
                Internal use only
              </p>
              <h2 className="mt-2 flex items-center gap-2 font-display text-[19px] font-semibold tracking-tight text-paper" data-id="element-102" data-mp="1rw10bj1ixs28">
                <SparklesIcon className="h-4 w-4 text-mint" aria-hidden="true" data-id="element-103" data-mp="1eu5qxlmr7v1k" />
                AI Interpretation
              </h2>
              <p className="mt-1.5 text-[11.5px] leading-snug text-sky" data-id="element-104" data-mp="1tsg67ke69fsf">
                What could these results mean for AbbVie?
              </p>
            </div>
            <button type="button" onClick={onClose} aria-label="Close interpretation" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-paper/30 text-paper/80 transition-colors duration-150 hover:border-mint hover:text-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-mint" data-id="element-105" data-mp="22s36g2v60fjw">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-106" data-mp="2likq7hy0s0t4" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-107" data-mp="2hhiwthqsaiz2">
          {running || !result ? <div className="flex flex-col gap-3" aria-live="polite" data-id="element-108" data-mp="304rsokxsbvqj">
              <p className="flex items-center gap-2 text-[12px] text-mut" data-id="element-109" data-mp="303gponc5ys0h">
                <RefreshCwIcon className="h-3.5 w-3.5 animate-spin text-blue" aria-hidden="true" data-id="element-110" data-mp="353up5gzp8tmw" />
                Reading the live responses…
              </p>
              {[0, 1, 2].map(i => <div key={i} className="rounded-xl border border-line bg-mist px-4 py-4" data-id="element-111" data-mp="2w1v48dlkd5eo">
                  <div className="h-2.5 w-1/3 rounded-full bg-line" data-id="element-112" data-mp="2hd8r8ijespqm" />
                  <div className="mt-3 h-2 w-full rounded-full bg-line" data-id="element-113" data-mp="2m5s39b2g7ctb" />
                  <div className="mt-2 h-2 w-5/6 rounded-full bg-line" data-id="element-114" data-mp="28e3pl3cjwga8" />
                </div>)}
            </div> : <div className="flex flex-col gap-6" data-id="element-115" data-mp="2cgiaru3ozs4o">
              <p className="text-[10.5px] leading-snug text-mut" data-id="element-116" data-mp="1yfxqeti4po92">
                Generated {result.generatedAt} from {result.sampleSize} responses in the live pulse.
              </p>

              {result.sections.map((section, i) => {
            const isCaution = section.id === 'cautions';
            return <motion.section key={section.id} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              delay: i * 0.05,
              ease: EASE
            }} className={isCaution ? 'rounded-xl border border-amber/40 bg-amber/[0.08] px-4 py-4' : ''} data-id="element-117" data-mp="31qlbp52wdiz4" data-mpi={i}>
                    <h3 className="flex items-baseline gap-2 font-display text-[14px] font-semibold tracking-tight text-navy" data-id="element-118" data-mp="2e0ze2m26bbt8">
                      <span className="font-display text-[11px] font-bold tabular-nums text-blue" data-id="element-119" data-mp="2937ohyp041s7">
                        {i + 1}
                      </span>
                      {section.title}
                    </h3>

                    {section.paragraphs.map(text => <p key={text} className="mt-2.5 text-[12.5px] leading-relaxed text-navy2" data-id="element-120" data-mp="euu2le6l1jez">
                        {text}
                      </p>)}

                    {section.bullets && <ul className="mt-3 flex flex-col gap-1.5" data-id="element-121" data-mp="ol6mr5ahjakl">
                        {section.bullets.map(bullet => <li key={bullet} className="flex gap-2 text-[12px] leading-snug text-navy2" data-id="element-122" data-mp="tif3d1fzj6jq">
                            <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${isCaution ? 'bg-amber' : 'bg-blue'}`} aria-hidden="true" data-id="element-123" data-mp="v2t9qr0gr7b" />
                            {bullet}
                          </li>)}
                      </ul>}
                  </motion.section>;
          })}
            </div>}
        </div>

        <footer className="shrink-0 border-t border-line px-6 py-4" data-id="element-124" data-mp="3dc7h47drcezu">
          <button type="button" onClick={run} disabled={running} className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-navy text-[13px] font-semibold text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2 disabled:opacity-40" data-id="element-125" data-mp="3de1satz88sqq">
            <RefreshCwIcon className={`h-3.5 w-3.5 ${running ? 'animate-spin' : ''}`} aria-hidden="true" data-id="element-126" data-mp="2ujmqyxpg0nzq" />
            Refresh interpretation
          </button>
          <p className="mt-2.5 text-[9.5px] leading-snug text-mut" data-id="element-127" data-mp="33kb6xxyecyf9">
            Directional hypothesis generated from congress responses. Not for external use or
            promotional claims.
          </p>
        </footer>
      </motion.aside>
    </motion.div>;
}